import { supabase } from './supabase';
import { UserSubscription, SubscriptionTransaction } from '../types/auth';

export const subscriptionService = {
  // Get user's active subscriptions
  async getUserSubscriptions(userId: string): Promise<UserSubscription[]> {
    const { data, error } = await supabase
      .from('user_subscriptions')
      .select('*')
      .eq('user_id', userId)
      .eq('status', 'active')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching user subscriptions:', error);
      throw new Error('Failed to fetch subscriptions');
    }

    return data || [];
  },

  // Get subscription details
  async getSubscription(subscriptionId: string): Promise<UserSubscription | null> {
    const { data, error } = await supabase
      .from('user_subscriptions')
      .select('*')
      .eq('id', subscriptionId)
      .single();

    if (error) {
      console.error('Error fetching subscription:', error);
      throw new Error('Failed to fetch subscription');
    }

    return data;
  },

  // Get subscription transactions
  async getSubscriptionTransactions(subscriptionId: string): Promise<SubscriptionTransaction[]> {
    const { data, error } = await supabase
      .from('subscription_transactions')
      .select('*')
      .eq('subscription_id', subscriptionId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching subscription transactions:', error);
      throw new Error('Failed to fetch subscription transactions');
    }

    return data || [];
  },

  // Create subscription checkout session
  async createSubscriptionCheckout(
    tokenPackageId: string,
    userId: string,
    successUrl: string,
    cancelUrl: string,
    metadata?: any
  ): Promise<{ url: string; session_id: string }> {
    const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/create-subscription`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({
        tokenPackageId,
        userId,
        successUrl,
        cancelUrl,
        metadata,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create subscription checkout');
    }

    const data = await response.json();
    return {
      url: data.url,
      session_id: data.session_id,
    };
  },

  // Cancel subscription
  async cancelSubscription(subscriptionId: string): Promise<void> {
    const { error } = await supabase
      .from('user_subscriptions')
      .update({
        cancel_at_period_end: true,
        updated_at: new Date().toISOString(),
      })
      .eq('id', subscriptionId);

    if (error) {
      console.error('Error canceling subscription:', error);
      throw new Error('Failed to cancel subscription');
    }
  },

  // Reactivate subscription
  async reactivateSubscription(subscriptionId: string): Promise<void> {
    const { error } = await supabase
      .from('user_subscriptions')
      .update({
        cancel_at_period_end: false,
        updated_at: new Date().toISOString(),
      })
      .eq('id', subscriptionId);

    if (error) {
      console.error('Error reactivating subscription:', error);
      throw new Error('Failed to reactivate subscription');
    }
  },

  // Get subscription statistics
  async getSubscriptionStats(): Promise<{
    total_subscriptions: number;
    active_subscriptions: number;
    canceled_subscriptions: number;
    monthly_revenue_cents: number;
  }> {
    const { data, error } = await supabase
      .from('subscription_stats')
      .select('*')
      .single();

    if (error) {
      console.error('Error fetching subscription stats:', error);
      throw new Error('Failed to fetch subscription statistics');
    }

    return data || {
      total_subscriptions: 0,
      active_subscriptions: 0,
      canceled_subscriptions: 0,
      monthly_revenue_cents: 0,
    };
  },

  // Format subscription interval for display
  formatSubscriptionInterval(interval: string, count: number = 1): string {
    if (count === 1) {
      return interval === 'month' ? 'Monthly' : 'Yearly';
    }
    return `Every ${count} ${interval}${count > 1 ? 's' : ''}`;
  },

  // Format subscription price
  formatSubscriptionPrice(priceCents: number, interval: string, count: number = 1): string {
    const price = priceCents / 100;
    if (interval === 'month') {
      return count === 1 ? `$${price}/month` : `$${price} every ${count} months`;
    } else {
      return count === 1 ? `$${price}/year` : `$${price} every ${count} years`;
    }
  },

  // Calculate savings for yearly subscriptions
  calculateYearlySavings(monthlyPriceCents: number, yearlyPriceCents: number): number {
    const monthlyTotal = monthlyPriceCents * 12;
    const savings = monthlyTotal - yearlyPriceCents;
    return Math.round((savings / monthlyTotal) * 100);
  },
}; 